import type { EditableDataTableColumn } from "@/components/editable-data-table";
import type { InvoiceItem } from "@/types";
import { formatCurrencyWithSymbol } from "@/utils/currency";
import { parseNumericInput } from "@/utils/date";

function sanitizeNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return numericValue;
}

export function createItemColumns(
  canEditItems: boolean,
  currency: string,
  unitOptions: { label: string; value: string }[]
): EditableDataTableColumn<InvoiceItem>[] {
  const columns: EditableDataTableColumn<InvoiceItem>[] = [];

  // description_extracted - editable para supervisores
  columns.push({
    id: "description_extracted",
    header: "Descripción",
    accessor: "description_extracted",
    className: "w-40",
    truncate: true,
    editable: canEditItems ? { type: "text" } : undefined
  });

  // quantity - supervisor only
  columns.push({
    id: "quantity",
    header: "Cantidad",
    accessor: (row) => row.quantity?.toString() ?? "-",
    editable: canEditItems ? { type: "number" } : undefined
  });

  // unit - supervisor only
  columns.push({
    id: "unit",
    header: "Unidad",
    accessor: "unit",
    editable: canEditItems
      ? {
          type: "async-combobox",
          placeholder: "Seleccionar unidad",
          loadOptions: (query: string) => {
            const normalizedQuery = query.toLowerCase().trim();
            if (!normalizedQuery) {
              return Promise.resolve(unitOptions);
            }
            return Promise.resolve(
              unitOptions.filter(
                (option) =>
                  option.label.toLowerCase().includes(normalizedQuery) ||
                  option.value.toLowerCase().includes(normalizedQuery)
              )
            );
          },
          getCurrentOption: (row) => {
            const currentUnit = row.unit;
            if (!currentUnit) return null;
            const option = unitOptions.find((opt) => opt.value === currentUnit);
            return option ?? { label: currentUnit, value: currentUnit };
          },
        }
      : undefined,
  });

  // unit_price - supervisor only
  columns.push({
    id: "unit_price",
    header: "P. Unitario",
    renderCell: (row) => formatCurrencyWithSymbol(row.unit_price, currency),
    editable: canEditItems ? { type: "number" } : undefined
  });

  // total_price - read-only (calculated)
  columns.push({
    id: "total_price",
    header: "Total",
    renderCell: (row) => {
      const totalPrice = sanitizeNumber(row.total_price);
      const quantity = sanitizeNumber(row.quantity);
      const unitPrice = sanitizeNumber(row.unit_price);

      const base =
        totalPrice ?? (quantity !== null && unitPrice !== null
          ? quantity * unitPrice
          : null);

      if (base === null) {
        return "-";
      }

      return formatCurrencyWithSymbol(base, currency);
    },
    editable: undefined
  });

  // vat_rate - supervisor only
  columns.push({
    id: "vat_rate",
    header: "IVA %",
    accessor: (row) => row.vat_rate?.toString() ?? "-",
    editable: canEditItems
      ? {
          type: "number",
          inputMode: "numeric",
          allowDecimal: false,
          onCommitTransform: (value) => Math.round(parseNumericInput(value)),
        }
      : undefined
  });

  return columns;
}
